import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import {
  FilePlus,
  FolderOpen,
  Upload,
  ClipboardList,
  Users,
  Link2,
  Share2,
  Palette,
  Flag,
  Clock,
} from "lucide-react"

const Wrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background-color: #f8f9fa;
  padding: 32px 40px;
`

const Inner = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
`

const Greeting = styled.div`
  h1 {
    margin: 0 0 6px 0;
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
  }
  p {
    margin: 0;
    font-size: 14px;
    color: #6b7280;
  }
`

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const ActionCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.05s;

  &:hover {
    border-color: #2563eb;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.08);
  }

  &:active {
    transform: translateY(1px);
  }
`

const ActionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${(p) => p.bg || "#eff6ff"};
  color: ${(p) => p.color || "#2563eb"};
  flex-shrink: 0;
`

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  strong {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
  }
  span {
    font-size: 12px;
    color: #6b7280;
  }
`

const Section = styled.section`
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px 24px;
`

const SectionTitle = styled.h2`
  margin: 0 0 14px 0;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
`

const RecentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`

const RecentItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.1s;

  & + & {
    border-top: 1px solid #f3f4f6;
  }

  &:hover {
    background-color: #f9fafb;
  }
`

const RecentTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RecentMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
  margin-left: 12px;
`

const EmptyHint = styled.div`
  padding: 16px 8px;
  font-size: 13px;
  color: #9ca3af;
`

const GuideGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const GuideCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  background-color: #f9fafb;
  border-radius: 8px;
`

const GuideHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
`

const GuideBody = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: #6b7280;
`

const formatRelative = (iso) => {
  if (!iso) return ""
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ""
  const diff = Date.now() - d.getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return "방금 전"
  if (min < 60) return `${min}분 전`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}시간 전`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}일 전`
  return d.toLocaleDateString("ko-KR")
}

export default function WelcomeDashboard({
  onNewChart,
  onLoadChart,
  onOpenLoadModal,
  onImportFile,
}) {
  const fileInputRef = useRef(null)
  const [recent, setRecent] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const res = await fetch("/request/gantt-charts", { credentials: "include" })
        const data = await res.json()
        if (!alive) return
        if (data.success && Array.isArray(data.charts)) {
          setRecent(data.charts.slice(0, 5))
        }
      } catch (e) {
        console.error("최근 차트 조회 실패:", e)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  return (
    <Wrapper>
      <Inner>
        <Greeting>
          <h1>👋 간트차트에 오신 걸 환영합니다</h1>
          <p>새 차트를 시작하거나 저장된 차트를 이어서 작업할 수 있어요.</p>
        </Greeting>

        <ActionRow>
          <ActionCard onClick={onNewChart}>
            <ActionIcon bg="#eff6ff" color="#2563eb">
              <FilePlus size={20} />
            </ActionIcon>
            <ActionText>
              <strong>새 차트 만들기</strong>
              <span>빈 캔버스에서 바로 시작</span>
            </ActionText>
          </ActionCard>

          <ActionCard onClick={onOpenLoadModal}>
            <ActionIcon bg="#f0fdf4" color="#16a34a">
              <FolderOpen size={20} />
            </ActionIcon>
            <ActionText>
              <strong>저장된 차트 불러오기</strong>
              <span>이전에 작업하던 차트 열기</span>
            </ActionText>
          </ActionCard>

          <ActionCard onClick={() => fileInputRef.current?.click()}>
            <ActionIcon bg="#fef3c7" color="#d97706">
              <Upload size={20} />
            </ActionIcon>
            <ActionText>
              <strong>JSON 파일 가져오기</strong>
              <span>내보낸 파일을 다시 불러오기</span>
            </ActionText>
            <input
              type="file"
              ref={fileInputRef}
              accept=".json"
              style={{ display: "none" }}
              onChange={onImportFile}
            />
          </ActionCard>
        </ActionRow>

        <Section>
          <SectionTitle>최근 차트</SectionTitle>
          {loading ? (
            <EmptyHint>불러오는 중…</EmptyHint>
          ) : recent.length === 0 ? (
            <EmptyHint>아직 저장된 차트가 없어요. 새 차트를 만들어 시작해보세요.</EmptyHint>
          ) : (
            <RecentList>
              {recent.map((c) => (
                <RecentItem key={c.id} onClick={() => onLoadChart(c.id)}>
                  <RecentTitle>{c.title}</RecentTitle>
                  <RecentMeta>
                    <Clock size={12} />
                    {formatRelative(c.updated_at)}
                  </RecentMeta>
                </RecentItem>
              ))}
            </RecentList>
          )}
        </Section>

        <Section>
          <SectionTitle>주요 기능 한눈에 보기</SectionTitle>
          <GuideGrid>
            <GuideCard>
              <GuideHead>
                <ClipboardList size={16} color="#2563eb" /> 작업 & 마일스톤
              </GuideHead>
              <GuideBody>
                사이드바에서 작업과 마일스톤을 추가하고, 타임라인에서 드래그로 일정을 조정하세요.
              </GuideBody>
            </GuideCard>
            <GuideCard>
              <GuideHead>
                <Users size={16} color="#16a34a" /> 담당자 관리
              </GuideHead>
              <GuideBody>
                헤더의 “담당자 관리”에서 팀원을 등록하고 작업별로 담당을 할당할 수 있어요.
              </GuideBody>
            </GuideCard>
            <GuideCard>
              <GuideHead>
                <Link2 size={16} color="#9333ea" /> 의존성
              </GuideHead>
              <GuideBody>
                선행 작업을 연결해 작업 간 흐름을 시각화하세요. 화살표로 자동 표시됩니다.
              </GuideBody>
            </GuideCard>
            <GuideCard>
              <GuideHead>
                <Share2 size={16} color="#0ea5e9" /> 실시간 협업
              </GuideHead>
              <GuideBody>
                저장된 차트는 “공유” 버튼으로 링크를 보내 팀과 함께 동시에 편집할 수 있어요.
              </GuideBody>
            </GuideCard>
            <GuideCard>
              <GuideHead>
                <Palette size={16} color="#d97706" /> 색상 설정
              </GuideHead>
              <GuideBody>
                작업 타입별 색상을 커스터마이즈해서 한눈에 구분되는 일정을 만들어보세요.
              </GuideBody>
            </GuideCard>
            <GuideCard>
              <GuideHead>
                <Flag size={16} color="#ef4444" /> 뷰 전환
              </GuideHead>
              <GuideBody>
                일/주/월 단위로 줌과 뷰 모드를 바꾸고, WBS 뷰로 계층 구조도 확인할 수 있어요.
              </GuideBody>
            </GuideCard>
          </GuideGrid>
        </Section>
      </Inner>
    </Wrapper>
  )
}
